import { zodResolver } from '@hookform/resolvers/zod';
import { serialize, useOnUpdate, useStateMerger } from '@wyz/utils';
import { useCallback, useEffect, useEffectEvent, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { Matomo } from '~/common/helpers';
import { IUserCustomer } from '~/common/types';
import { useManageSearchHistory } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import {
  CheckoutTimer,
  DEFAULT_VEHICLE_TYPE,
  PurchaseSearchHelpers,
  PurchaseTracking,
  SearchHistoryHelpers,
} from '~/purchase/helpers';
import { baseSearchFormSchema } from '~/purchase/schemas';
import { IBaseSearchFormSchema, IProductListState } from '~/purchase/types';

type Args = {
  customer: IUserCustomer;
  defaultValues?: Partial<IBaseSearchFormSchema>;
};
export const usePurchaseSearchForm = ({ customer, defaultValues }: Args) => {
  const manageSearchHistory = useManageSearchHistory({
    create: { enabled: true },
  });
  const purchaseContext = usePurchaseContext();

  const [searchParams] = useSearchParams();
  const methods = useForm<IBaseSearchFormSchema>({
    resolver: zodResolver(baseSearchFormSchema),
    defaultValues: {
      ...PurchaseSearchHelpers.getTyreSearchDefaultValues(), // raw defaults
      seasons: customer.settingTemplate?.generalSetting?.defaultSeasons ?? [], // should be before qs defaults since it can be override by qs
      ...PurchaseSearchHelpers.parseSearchFromQs(searchParams), // qs defaults
      ...defaultValues, // functionnal defaults
      // forced defaults because we stilll have only one "univer" aka vehicle type
      vehicleTypeGroup:
        customer.settingTemplate?.vehicleTypeGroups.find(
          (group) => group.name === DEFAULT_VEHICLE_TYPE,
        )?.name || customer.settingTemplate?.vehicleTypeGroups[0]?.name,
    },
    shouldUnregister: false,
  });
  const watchedVehicleTypeGroup = useWatch({
    control: methods.control,
    name: 'vehicleTypeGroup',
  });
  const vehicleTypes = customer.settingTemplate?.vehicleTypeGroups;
  const relatedTypes = useMemo(
    () =>
      watchedVehicleTypeGroup && vehicleTypes
        ? vehicleTypes.find((item) => item.name === watchedVehicleTypeGroup)
            ?.vehicleTypeIds
        : [],
    [vehicleTypes, watchedVehicleTypeGroup],
  );

  const [listState, setter] = useStateMerger<IProductListState>(
    PurchaseSearchHelpers.DEFAULT_PRODUCT_LIST_STATE,
  );
  const navigate = useNavigate();

  const handleOnSearch = useCallback(
    (query: Partial<IProductListState>) => {
      if (query.searchQuery) {
        manageSearchHistory.create.mutate(
          SearchHistoryHelpers.buildPurchasePayload(query.searchQuery),
        );
        CheckoutTimer.markFirstSearchIfAbsent();
        Matomo.suppressNextPageView();
        PurchaseTracking.trackSearchSubmitted(query.searchQuery);
      }
      const resultPath = purchaseContext.isCallCenterMode
        ? '/call-center/result'
        : '/purchase/result';
      const searchParams = serialize(
        PurchaseSearchHelpers.serializeSearchToQs(query.searchQuery),
      );

      void navigate({
        pathname: resultPath,
        search: searchParams,
      });

      setter(query);
    },
    [
      manageSearchHistory.create,
      navigate,
      purchaseContext.isCallCenterMode,
      setter,
    ],
  );

  const setValue = useEffectEvent(methods.setValue);
  const resetForm = useEffectEvent(() => {
    setter(PurchaseSearchHelpers.DEFAULT_PRODUCT_LIST_STATE);

    methods.reset({
      ...PurchaseSearchHelpers.getTyreSearchDefaultValues(),
      vehicleTypeGroup: methods.getValues('vehicleTypeGroup'),
      vehicleTypes: relatedTypes,
      seasons: customer.settingTemplate?.generalSetting?.defaultSeasons ?? [],
    });
  });
  useOnUpdate(() => {
    if (customer.id) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      resetForm();
    }
  }, [customer.id]);

  useEffect(() => {
    if (watchedVehicleTypeGroup && relatedTypes) {
      setValue('vehicleTypes', relatedTypes);
    }
  }, [watchedVehicleTypeGroup, relatedTypes]);

  return {
    vehicleTypes: relatedTypes ?? [],
    vehicleTypesGroups: customer.settingTemplate?.vehicleTypeGroups ?? [],
    methods,
    listState,
    setter,
    onSubmit: handleOnSearch,
  };
};
